/**
 * Controles de reproducción y manipulación de playlists/tracks.
 *
 * Esto es el "cerebro" del player: qué pasa al pulsar cada botón, al
 * cambiar de pista, al añadir/quitar canciones, al eliminar una playlist.
 *
 * Migrado fielmente desde el legacy. Cambios:
 *  - Tipado fuerte (state, tracks, playlists).
 *  - El timer de scrub vive en este módulo (closure local, no en state).
 *  - selectTrack/selectPlaylist/etc. exportadas para usar como callbacks
 *    desde los renderers.
 */

import { musicAudio, playSfx, stopSfx } from './audio.js';
import { savePlaylists } from './persistence.js';
import { renderFileList, renderFolderList, renderPlaylists } from './render.js';
import {
  getPlaylist,
  getTrack,
  setActivePlaylist,
  setCurrentTrack,
  setPaused,
  setPlaying,
  setPlaylists,
  setScrubMode,
  state,
} from './state.js';

// ─── Timer de scrub (closure local) ──────────────────────────────────────

let scrubTimer: number | null = null;

// ─── Helpers visuales ────────────────────────────────────────────────────

/** Aplica un flash visual de "pulsado" durante 130ms a un hotspot. */
export function flashHotspot(elId: string): void {
  const el = document.getElementById(elId);
  if (!el) return;
  el.classList.add('r-pressed');
  window.setTimeout(() => el.classList.remove('r-pressed'), 130);
}

/**
 * Sincroniza la UI del player con el estado actual: LEDs, botones
 * iluminados/pulsados, reels girando/parados.
 */
export function updatePlayState(): void {
  const led = document.getElementById('r-led');
  const ledPl = document.getElementById('r-led-play');
  const play = document.getElementById('r-hs-play');
  const pause = document.getElementById('r-hs-pause');
  const rl = document.getElementById('r-reel-l');
  const rr = document.getElementById('r-reel-r');
  const rew = document.getElementById('r-hs-rew');
  const ff = document.getElementById('r-hs-ff');

  // LEDs encendidos: cuando hay una pista cargada y estamos reproduciendo o pausados
  const lit = state.isPlaying || state.isPaused;
  if (led) led.classList.toggle('r-on', lit);
  if (ledPl) ledPl.classList.toggle('r-on', lit);

  // Botón PLAY iluminado mientras suena, "stuck" mientras está en pausa
  if (play) {
    play.classList.toggle('r-lit', state.isPlaying);
    play.classList.toggle('r-stuck', state.isPaused);
  }
  if (pause) pause.classList.toggle('r-stuck', state.isPaused);

  // REW / FF "stuck" durante el scrub
  if (rew) rew.classList.toggle('r-stuck', state.scrubMode === 'rew');
  if (ff) ff.classList.toggle('r-stuck', state.scrubMode === 'ff');

  // Reels: solo giran con reproducción real, NO en pausa. Más rápido en scrub.
  // En REW (retroceso) la animación se invierte vía .r-rev (retoque #2).
  const spinning = state.isPlaying || Boolean(state.scrubMode);
  const reverse = state.scrubMode === 'rew';
  if (rl) {
    rl.classList.toggle('r-go', spinning);
    rl.classList.toggle('r-scrub', Boolean(state.scrubMode));
    rl.classList.toggle('r-rev', reverse);
  }
  if (rr) {
    rr.classList.toggle('r-go', spinning);
    rr.classList.toggle('r-scrub', Boolean(state.scrubMode));
    rr.classList.toggle('r-fast', true);
    rr.classList.toggle('r-rev', reverse);
  }

  // LEDs del hotspot radio en la sala: encendidos sólo mientras suena.
  document.body.classList.toggle('radio-on', state.isPlaying);
}

// ─── Reproducción ────────────────────────────────────────────────────────

/**
 * Selecciona una pista por id sin reproducirla. La marca como activa
 * (`currentTrackId`), la carga en el `<audio>` y refresca la UI. El
 * usuario tiene que pulsar PLAY o hacer doble click para que suene
 * — comportamiento estilo reproductor real de música.
 *
 * Si había una pista sonando o pausada, se detiene al cambiar de
 * selección (no tiene sentido seguir oyendo la anterior).
 */
export function selectTrack(id: string): void {
  const t = getTrack(state, id);
  if (!t) return;
  setCurrentTrack(id);
  musicAudio.pause();
  setPlaying(false);
  setPaused(false);
  musicAudio.src = t.file;
  musicAudio.load();
  updatePlayState();
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
}

/**
 * Selecciona una pista Y la reproduce. Lo que dispara el doble click
 * sobre un nombre y lo que llama `nextTrackAndPlay()` al final de la
 * pista actual.
 */
export function playTrack(id: string): void {
  const t = getTrack(state, id);
  if (!t) return;
  setCurrentTrack(id);
  setPlaying(false);
  setPaused(false);
  musicAudio.pause();
  musicAudio.src = t.file;
  musicAudio.load();
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
  doPlay();
}

/** Cambia la playlist activa. Reproduce SFX de "insertar cassette" si cambia. */
export function selectPlaylist(pid: string): void {
  if (pid !== state.activePlaylistId) {
    playSfx('insert');
  }
  setActivePlaylist(pid);
  renderPlaylists(getCallbacks());
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
}

/**
 * Internal: arranca la reproducción del audio cargado. Si no hay audio
 * cargado, intenta cargar y reproducir la primera pista de la playlist
 * activa — comportamiento "tocar PLAY desde frío" del cassette.
 */
function doPlay(): void {
  if (!musicAudio.src || musicAudio.src === location.href) {
    const pl = getPlaylist(state, state.activePlaylistId);
    if (pl && pl.tracks.length) {
      playTrack(pl.tracks[0]!);
      return;
    }
    return;
  }
  musicAudio
    .play()
    .then(() => {
      setPlaying(true);
      updatePlayState();
    })
    .catch(() => {
      setPlaying(false);
      updatePlayState();
    });
}

/** Termina cualquier modo de scrub activo, restaurando audio normal. */
export function endScrub(): void {
  if (scrubTimer !== null) {
    window.clearInterval(scrubTimer);
    scrubTimer = null;
  }
  if (state.scrubMode) stopSfx('fast');
  setScrubMode(null);
  musicAudio.muted = false;
}

/** Botón PLAY: reproduce o reanuda según el estado. */
export function togglePlay(): void {
  if (state.editMode) return;
  playSfx('button');
  flashHotspot('r-hs-play');
  endScrub();
  if (state.isPaused) {
    setPaused(false);
    doPlay();
    return;
  }
  if (state.isPlaying) return; // ya suena, PLAY no hace nada (cassette real)
  doPlay();
}

/** Botón PAUSE: pausa estilo cassette (botón enganchado, LEDs siguen). */
export function pauseAudio(): void {
  if (state.editMode) return;
  playSfx('button');
  flashHotspot('r-hs-pause');
  endScrub();
  if (!state.isPlaying && !state.isPaused) return;
  if (state.isPaused) {
    // Segunda pulsación: reanudar
    setPaused(false);
    musicAudio
      .play()
      .then(() => {
        setPlaying(true);
        updatePlayState();
      })
      .catch(() => {
        setPlaying(false);
        updatePlayState();
      });
  } else {
    // Primera pulsación: pausar (PLAY y LEDs siguen)
    musicAudio.pause();
    setPlaying(false);
    setPaused(true);
    updatePlayState();
  }
}

/** Botón STOP: si hay scrub, lo termina y reanuda. Si no, para y rebobina. */
export function stopAudio(): void {
  if (state.editMode) return;
  playSfx('stop');
  flashHotspot('r-hs-stop');

  if (state.scrubMode) {
    endScrub();
    musicAudio
      .play()
      .then(() => {
        setPlaying(true);
        setPaused(false);
        updatePlayState();
      })
      .catch(() => {
        setPlaying(false);
        updatePlayState();
      });
    return;
  }

  // STOP normal: para el audio y libera PLAY/PAUSE, pero NO rebobina —
  // re-pulsar PLAY reanuda desde la misma posición. Como un cassette real:
  // STOP no es REW. La barra de progreso se queda donde estaba (la mantiene
  // el listener `timeupdate` de knob-progress.ts).
  musicAudio.pause();
  setPlaying(false);
  setPaused(false);
  updatePlayState();
}

/** REW / FF: avance/retroceso rápido con loop de SFX y velocidad ~15×. */
export function startScrub(direction: 'rew' | 'ff'): void {
  if (state.editMode) return;
  // Botón mecánico de cassette: una vez enganchado, solo STOP lo libera.
  // Re-pulsar REW estando en REW (o FF estando en FF) no hace nada.
  if (state.scrubMode === direction) return;
  endScrub();
  if (!musicAudio.src) return;
  playSfx('button');
  flashHotspot(direction === 'rew' ? 'r-hs-rew' : 'r-hs-ff');
  setScrubMode(direction);
  setPaused(false);
  musicAudio.muted = true;
  playSfx('fast', { loop: true });

  const STEP_MS = 90;
  const SECS_PER_TICK = 1.4; // ~15× velocidad real

  scrubTimer = window.setInterval(() => {
    if (!musicAudio.duration) return;
    const delta = direction === 'ff' ? SECS_PER_TICK : -SECS_PER_TICK;
    const t = musicAudio.currentTime + delta;
    if (t <= 0) {
      musicAudio.currentTime = 0;
      endScrub();
      if (state.isPlaying) musicAudio.play().catch(() => {});
      updatePlayState();
      return;
    }
    if (t >= musicAudio.duration) {
      // Llegamos al final: pasar a la siguiente pista
      endScrub();
      nextTrackAndPlay();
      return;
    }
    musicAudio.currentTime = t;
  }, STEP_MS);
  updatePlayState();
}

/** Pasa a la siguiente pista de la playlist y la reproduce. */
export function nextTrackAndPlay(): void {
  const pl = getPlaylist(state, state.activePlaylistId);
  if (!pl) return;
  const idx = pl.tracks.indexOf(state.currentTrackId ?? '');
  if (idx >= 0 && idx < pl.tracks.length - 1) {
    playTrack(pl.tracks[idx + 1]!);
  } else {
    musicAudio.pause();
    musicAudio.currentTime = 0;
    setPlaying(false);
    setPaused(false);
    updatePlayState();
  }
}

// ─── Manipulación de playlists ───────────────────────────────────────────

/** Botón SAVE: persiste y muestra "✓ SAVED" durante 1.5s. */
export function saveCurrentPlaylist(): void {
  savePlaylists();
  const btn = document.getElementById('r-save-btn');
  if (!btn) return;
  const original = btn.textContent ?? 'SAVE';
  btn.textContent = '✓ SAVED';
  window.setTimeout(() => {
    btn.textContent = original;
  }, 1500);
}

/** Elimina una playlist tras confirmación. La default no se puede eliminar. */
export function deletePlaylist(pid: string): void {
  const pl = getPlaylist(state, pid);
  if (!pl || pl.locked) return;
  if (!confirm(`¿Eliminar la playlist "${pl.name}"?`)) return;

  const filtered = state.playlists.filter((p) => p.id !== pid);
  setPlaylists(filtered);

  if (state.activePlaylistId === pid && filtered[0]) {
    setActivePlaylist(filtered[0].id);
  }

  savePlaylists();
  renderPlaylists(getCallbacks());
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
}

/**
 * Toggle: añade o quita la pista de la playlist activa. No-op si la
 * playlist activa es la del escuadrón (locked, server-backed).
 */
export function toggleTrackInActivePlaylist(tid: string): void {
  const pl = getPlaylist(state, state.activePlaylistId);
  if (!pl || pl.locked) return;
  const i = pl.tracks.indexOf(tid);
  if (i >= 0) pl.tracks.splice(i, 1);
  else pl.tracks.push(tid);
  savePlaylists();
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
  renderPlaylists(getCallbacks());
}

/**
 * Quita la pista de la playlist activa (sólo "quitar", no "toggle"). No-op
 * si la playlist activa es la del escuadrón (locked, server-backed).
 */
export function removeTrackFromActivePlaylist(tid: string): void {
  const pl = getPlaylist(state, state.activePlaylistId);
  if (!pl || pl.locked) return;
  const i = pl.tracks.indexOf(tid);
  if (i < 0) return;
  pl.tracks.splice(i, 1);

  if (state.currentTrackId === tid) {
    musicAudio.pause();
    setCurrentTrack(null);
    setPlaying(false);
    updatePlayState();
  }
  savePlaylists();
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
  renderPlaylists(getCallbacks());
}

// ─── Callbacks bundle ────────────────────────────────────────────────────

/**
 * Devuelve el bundle de callbacks que pasa cada renderer. Centralizado aquí
 * para que cualquier renderer dispare las acciones correctas sin que init.ts
 * tenga que saber qué callbacks van con cada uno.
 */
export function getCallbacks(): import('./render.js').RenderCallbacks {
  return {
    onSelectPlaylist: selectPlaylist,
    onDeletePlaylist: deletePlaylist,
    onSelectTrack: selectTrack,
    onPlayTrack: playTrack,
    onRemoveTrackFromPlaylist: removeTrackFromActivePlaylist,
    onToggleTrackInPlaylist: toggleTrackInActivePlaylist,
  };
}
